import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import clientRequirementsService from '../../../services/clientRequirementsService';

const RequirementDetailModal = ({
  requirement,
  isOpen = false,
  onClose,
  onRequirementUpdated,
  onEditRequirement,
  onShowMatching
}) => {
  const [updatingResponse, setUpdatingResponse] = useState(null);
  const [responseNotes, setResponseNotes] = useState({});
  const [showNotesInput, setShowNotesInput] = useState({});
  
  if (!isOpen || !requirement) return null;

  // Normalise: list-level objects have company_name at root; detail-level objects have it under clients{}
  const clientInfo = requirement.clients || {
    company_name: requirement.company_name,
    contact_person: requirement.contact_person || requirement.client_contact,
    email: requirement.client_email || requirement.email,
    industry: requirement.industry
  };

  const handleUpdateResponse = async (submissionId, response) => {
    try {
      setUpdatingResponse(submissionId);
      const notes = responseNotes[submissionId] || '';
      
      const result = await clientRequirementsService.updateClientResponse(submissionId, response, notes);
      
      if (result?.success) {
        // Refresh requirement data
        if (onRequirementUpdated) {
          const updatedReq = await clientRequirementsService.getRequirement(requirement.id);
          if (updatedReq?.success) {
            onRequirementUpdated(updatedReq.data);
          }
        }
        setShowNotesInput({});
        setResponseNotes({});
      } else {
        alert(result?.error || 'Failed to update response');
      }
    } catch (error) {
      alert('An error occurred while updating response');
      console.error('Update response error:', error);
    } finally {
      setUpdatingResponse(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      open: 'bg-green-100 text-green-800 border-green-200',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
      partially_filled: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      completed: 'bg-gray-100 text-gray-800 border-gray-200',
      on_hold: 'bg-orange-100 text-orange-800 border-orange-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusStyles[status] || statusStyles.open}`}>
        {status?.replace('_', ' ') || 'Open'}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityStyles = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };

    const priorityIcons = {
      low: '🔵',
      medium: '🟡',
      high: '🟠',
      urgent: '🔴'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${priorityStyles[priority] || priorityStyles.medium}`}>
        <span className="mr-1">{priorityIcons[priority] || '🔵'}</span>
        {priority || 'Medium'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="relative inline-block transform overflow-hidden rounded-lg bg-background text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:align-middle">
          {/* Header */}
          <div className="border-b bg-background px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-foreground">
                  {requirement.title}
                </h3>
                <p className="text-muted-foreground mt-1">
                  {clientInfo?.company_name} • {requirement.position_title}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {getStatusBadge(requirement.status)}
                {getPriorityBadge(requirement.priority)}
                <Button
                  variant="outline"
                  size="sm"
                  iconName="X"
                  onClick={onClose}
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-[600px] overflow-y-auto">
            <div className="space-y-8">
              {/* Client Information */}
              <div>
                <h4 className="text-lg font-medium text-foreground mb-4">Client Information</h4>
                <div className="bg-muted/20 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Company:</span>
                    <p className="text-foreground">{clientInfo?.company_name}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Contact Person:</span>
                    <p className="text-foreground">{clientInfo?.contact_person}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Email:</span>
                    <p className="text-foreground">{clientInfo?.email}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Industry:</span>
                    <p className="text-foreground">{clientInfo?.industry}</p>
                  </div>
                </div>
              </div>

              {/* Position Details */}
              <div>
                <h4 className="text-lg font-medium text-foreground mb-4">Position Details</h4>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Position:</span>
                      <p className="text-foreground">{requirement.position_title}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Department:</span>
                      <p className="text-foreground">{requirement.department || 'Not specified'}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Quantity Needed:</span>
                      <p className="text-foreground">{requirement.quantity_needed} position{requirement.quantity_needed !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Experience Level:</span>
                      <p className="text-foreground">{requirement.experience_level}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Employment Type:</span>
                      <p className="text-foreground capitalize">{requirement.employment_type?.replace('_', ' ') || 'Full Time'}</p>
                    </div>
                  </div>

                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Job Description:</span>
                    <div className="mt-2 p-4 bg-muted/20 rounded-lg">
                      <p className="text-foreground whitespace-pre-wrap">{requirement.job_description}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Skills & Requirements */}
              <div>
                <h4 className="text-lg font-medium text-foreground mb-4">Skills & Requirements</h4>
                <div className="space-y-4">
                  {(requirement.required_skills || []).length > 0 && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Required Skills:</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(requirement.required_skills || []).map((skill, idx) => (
                          <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 border border-blue-200">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(requirement.preferred_skills || []).length > 0 && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Preferred Skills:</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(requirement.preferred_skills || []).map((skill, idx) => (
                          <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800 border border-green-200">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {requirement.education_requirements && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Education Requirements:</span>
                      <p className="text-foreground mt-1">{requirement.education_requirements}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Location & Budget */}
              <div>
                <h4 className="text-lg font-medium text-foreground mb-4">Location & Compensation</h4>
                <div className="bg-muted/20 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Location:</span>
                    <p className="text-foreground">📍 {requirement.location}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Budget Range:</span>
                    <p className="text-foreground">
                      {requirement.budget_min || requirement.budget_max ? (
                        `${formatCurrency(requirement.budget_min)} - ${formatCurrency(requirement.budget_max)}`
                      ) : (
                        'Not specified'
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Start Date:</span>
                    <p className="text-foreground">{formatDate(requirement.start_date)}</p>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h4 className="text-lg font-medium text-foreground mb-4">Timeline</h4>
                <div className="bg-muted/20 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Deadline:</span>
                    <p className="text-foreground">{formatDate(requirement.deadline)}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Created:</span>
                    <p className="text-foreground">{formatDate(requirement.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* Additional Requirements */}
              {requirement.additional_requirements && (
                <div>
                  <h4 className="text-lg font-medium text-foreground mb-4">Additional Requirements</h4>
                  <div className="p-4 bg-muted/20 rounded-lg">
                    <p className="text-foreground whitespace-pre-wrap">{requirement.additional_requirements}</p>
                  </div>
                </div>
              )}

              {/* Matching Summary */}
              <div>
                <h4 className="text-lg font-medium text-foreground mb-4">Matching Summary</h4>
                <div className="bg-muted/20 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Total Matches:</span>
                      <p className="text-2xl font-bold text-foreground">{(requirement.candidate_matches || []).length}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Best Match Score:</span>
                      <p className="text-2xl font-bold text-blue-600">
                        {(requirement.candidate_matches || []).length > 0 
                          ? `${Math.max(...(requirement.candidate_matches || []).map(m => m.compatibility_score || 0))}%`
                          : '0%'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Linked Leads Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-foreground">Linked Leads</h4>
                  <span className="text-sm text-muted-foreground">
                    {(requirement.linked_leads || []).length} lead{(requirement.linked_leads || []).length !== 1 ? 's' : ''} sent
                  </span>
                </div>
                {(requirement.linked_leads || []).length > 0 ? (
                  <div className="space-y-3">
                    {(requirement.linked_leads || []).map((submission) => (
                      <div key={submission.id} className="bg-muted/20 rounded-lg p-4 border border-border">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-sm font-medium text-primary">
                                  {submission.lead_name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-foreground">{submission.lead_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {submission.lead_email || submission.lead_phone}
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                              <div>
                                <span className="text-muted-foreground">Experience:</span>
                                <p className="font-medium">{submission.experience_years || 0} years</p>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Level:</span>
                                <p className="font-medium capitalize">{submission.skill_level || 'N/A'}</p>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Status:</span>
                                <p className="font-medium capitalize">{submission.lead_status?.replace('_', ' ') || 'N/A'}</p>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Response:</span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ml-1 ${
                                  submission.client_response === 'accepted' ? 'bg-green-100 text-green-800' :
                                  submission.client_response === 'rejected' ? 'bg-red-100 text-red-800' :
                                  submission.client_response === 'under_review' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {submission.client_response === 'accepted' ? 'Accepted' :
                                   submission.client_response === 'rejected' ? 'Rejected' :
                                   submission.client_response === 'under_review' ? 'Under Review' :
                                   'Pending'}
                                </span>
                              </div>
                            </div>
                            {(submission.skills || []).length > 0 && (
                              <div className="mt-2">
                                <span className="text-xs text-muted-foreground">Skills: </span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {(submission.skills || []).slice(0, 5).map((skill, idx) => (
                                    <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                                      {skill}
                                    </span>
                                  ))}
                                  {(submission.skills || []).length > 5 && (
                                    <span className="text-xs text-muted-foreground">
                                      +{(submission.skills || []).length - 5} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          {/* Response Actions */}
                          {submission.client_response === 'pending' || submission.client_response === 'under_review' ? (
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowNotesInput({...showNotesInput, [submission.id]: !showNotesInput[submission.id]})}
                                className="text-xs"
                              >
                                <Icon name="MessageSquare" size={14} className="mr-1" />
                                Add Notes
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUpdateResponse(submission.id, 'accepted')}
                                disabled={updatingResponse === submission.id}
                                className="text-xs text-green-600 border-green-300 hover:bg-green-50"
                              >
                                {updatingResponse === submission.id ? (
                                  <Icon name="Loader" size={14} className="mr-1 animate-spin" />
                                ) : (
                                  <Icon name="CheckCircle" size={14} className="mr-1" />
                                )}
                                Accept
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUpdateResponse(submission.id, 'rejected')}
                                disabled={updatingResponse === submission.id}
                                className="text-xs text-red-600 border-red-300 hover:bg-red-50"
                              >
                                {updatingResponse === submission.id ? (
                                  <Icon name="Loader" size={14} className="mr-1 animate-spin" />
                                ) : (
                                  <Icon name="XCircle" size={14} className="mr-1" />
                                )}
                                Reject
                              </Button>
                            </div>
                          ) : null}
                        </div>
                        
                        {/* Notes Input */}
                        {showNotesInput[submission.id] && (
                          <div className="mt-3 pt-3 border-t border-border">
                            <textarea
                              value={responseNotes[submission.id] || submission.client_notes || ''}
                              onChange={(e) => setResponseNotes({...responseNotes, [submission.id]: e.target.value})}
                              placeholder="Add notes about this response..."
                              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm"
                              rows="2"
                            />
                            <div className="flex items-center gap-2 mt-2">
                              <Button
                                size="sm"
                                onClick={() => {
                                  if (submission.client_response === 'pending') {
                                    handleUpdateResponse(submission.id, 'under_review');
                                  } else {
                                    setShowNotesInput({...showNotesInput, [submission.id]: false});
                                  }
                                }}
                                className="text-xs"
                              >
                                Save Notes
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setShowNotesInput({...showNotesInput, [submission.id]: false});
                                  setResponseNotes({...responseNotes, [submission.id]: submission.client_notes || ''});
                                }}
                                className="text-xs"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between pt-3 border-t border-border text-xs text-muted-foreground">
                          <div>
                            <span>Sent: {submission.submitted_date ? new Date(submission.submitted_date).toLocaleString() : 'N/A'}</span>
                            {submission.submitted_by_name && (
                              <span className="ml-3">by {submission.submitted_by_name}</span>
                            )}
                          </div>
                          {submission.client_notes && (
                            <div className="text-right max-w-xs">
                              <span className="font-medium">Notes: </span>
                              <span className="truncate">{submission.client_notes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-muted/20 rounded-lg p-6 text-center border border-dashed border-border">
                    <p className="text-muted-foreground">No leads have been sent to this requirement yet</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Leads sent from CRM Lead Management will appear here
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t bg-muted/20 px-6 py-4">
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button
                iconName="Users"
                iconPosition="left"
                onClick={() => {
                  onClose?.();
                  onShowMatching?.(requirement);
                }}
              >
                View Matches
              </Button>
              <Button
                variant="outline"
                iconName="Edit"
                iconPosition="left"
                onClick={() => {
                  onClose?.();
                  onEditRequirement?.(requirement);
                }}
              >
                Edit Requirement
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequirementDetailModal;